import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getCurrencySymbolFromEnum } from 'lib/region/countryUtils';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';

/**
 * Local retail prices for the Ring AIR comparison table.
 * Sourced Aug 2026 from official stores / authorised retail:
 * - Apple Watch: apple.com regional stores (SE 3 GPS start → Ultra 3);
 *   JP from apple.com/jp (¥37,800 → Ultra 3 ¥129,800)
 * - Oura: ouraring.com membership page + India launch (Croma/Amazon.in)
 *   / UK / EU / AU / CA list prices; UAE Virgin/Amazon.ae;
 *   JP Ring 5 start ¥65,800 + membership ¥11,800/yr (SourceNext)
 * - Garmin: entry GPS watch → flagship (Fenix / Forerunner 970 class),
 *   matching the existing "$199-$999+" shape; JP Forerunner 70 → 970
 */
type RangePrice = { min: number; max: number };
type OuraPrice = { starting: number; subscriptionAnnual: number };

export type CompetitorComparisonPrices = {
  garmin: RangePrice;
  appleWatch: RangePrice;
  oura: OuraPrice;
};

const USD_PRICES: CompetitorComparisonPrices = {
  garmin: { min: 199, max: 999 },
  appleWatch: { min: 249, max: 799 },
  oura: { starting: 349, subscriptionAnnual: 69.99 },
};

export const COMPETITOR_COMPARISON_PRICES: Record<
  ShopifyStore,
  CompetitorComparisonPrices
> = {
  [ShopifyStore.US]: USD_PRICES,
  [ShopifyStore.ROW]: {
    ...USD_PRICES,
    oura: { starting: 349, subscriptionAnnual: 79 },
  },
  [ShopifyStore.IN]: {
    garmin: { min: 19990, max: 99990 },
    appleWatch: { min: 25900, max: 89900 },
    oura: { starting: 28900, subscriptionAnnual: 6999 },
  },
  [ShopifyStore.GB]: {
    garmin: { min: 179, max: 999 },
    appleWatch: { min: 219, max: 749 },
    oura: { starting: 349, subscriptionAnnual: 69.99 },
  },
  [ShopifyStore.EU]: {
    garmin: { min: 199, max: 999 },
    appleWatch: { min: 269, max: 899 },
    oura: { starting: 349, subscriptionAnnual: 69.99 },
  },
  [ShopifyStore.AE]: {
    garmin: { min: 749, max: 3699 },
    appleWatch: { min: 999, max: 3199 },
    oura: { starting: 1399, subscriptionAnnual: 290 },
  },
  [ShopifyStore.AU]: {
    garmin: { min: 299, max: 1499 },
    appleWatch: { min: 399, max: 1399 },
    oura: { starting: 569, subscriptionAnnual: 109.99 },
  },
  [ShopifyStore.CA]: {
    garmin: { min: 249, max: 1299 },
    appleWatch: { min: 329, max: 1099 },
    oura: { starting: 479, subscriptionAnnual: 89.99 },
  },
  [ShopifyStore.SA]: {
    garmin: { min: 749, max: 3699 },
    appleWatch: { min: 949, max: 2999 },
    oura: { starting: 1399, subscriptionAnnual: 290 },
  },
  [ShopifyStore.MX]: {
    garmin: { min: 3999, max: 19999 },
    appleWatch: { min: 5499, max: 19999 },
    oura: { starting: 6999, subscriptionAnnual: 1450 },
  },
  [ShopifyStore.ZA]: {
    garmin: { min: 3599, max: 17999 },
    appleWatch: { min: 5999, max: 16999 },
    oura: { starting: 6299, subscriptionAnnual: 1299 },
  },
  [ShopifyStore.JP]: {
    garmin: { min: 39800, max: 121800 },
    appleWatch: { min: 37800, max: 129800 },
    oura: { starting: 65800, subscriptionAnnual: 11800 },
  },
};

export type CompetitorComparisonCopy = {
  garmin: string;
  appleWatch: string;
  /** Interpolated into `ring.comparison.ouraRing.pricing` by the caller. */
  ouraStarting: string;
  ouraSubscription: string;
};

/**
 * Formatted price copy only — the Oura sentence is assembled at the callsite so
 * its i18n key stays a quoted literal that check-i18n-keys can see.
 */
export const getCompetitorComparisonCopy = (
  store: ShopifyStore | null,
  currency: string | null,
): CompetitorComparisonCopy => {
  const s = store ?? ShopifyStore.ROW;
  const c = (currency?.toLowerCase() as Currency) ?? Currency.usd;
  const symbol = getCurrencySymbolFromEnum(c);
  const prices = COMPETITOR_COMPARISON_PRICES[s] ?? USD_PRICES;

  const amount = (value: number) => `${symbol}${formatPriceWithComma(value, c)}`;
  const range = (r: RangePrice) => `${amount(r.min)}-${amount(r.max)}+`;

  return {
    garmin: range(prices.garmin),
    appleWatch: range(prices.appleWatch),
    ouraStarting: amount(prices.oura.starting),
    ouraSubscription: amount(prices.oura.subscriptionAnnual),
  };
};
